// Presign → upload → Shrine cached-upload JSON, for the private (/s3/params)
// and public (/s3/public_params) cache storages.
//
// Files at or above MULTIPART_THRESHOLD_BYTES on /s3/params go through
// multipart_upload.js (parallel parts, per-part retry); everything else is the
// single presigned PUT this app has always used, now over XHR so callers can
// get progress and cancel. Both paths resolve with the same JSON:
//   { id, storage: "cache" | "public_cache", metadata: { size, filename, mime_type } }
//
// components/input_file/input_file.js keeps its own jQuery copy of the single
// PUT for the image ponds; its large-file ponds import this module. Keep the
// presign contract (query params, /cache/ key parsing, JSON shape) in sync.
import { multipartUpload } from "./multipart_upload"
import { xhrPut, abortError, isAbortError, isRetryableStatus } from "./xhr"

// Re-exported so callers can recognise or raise a cancellation without
// importing the low-level helper.
export { abortError, isAbortError }

export var MULTIPART_THRESHOLD_BYTES = 128 * 1024 * 1024
var SINGLE_PUT_ATTEMPTS = 2

export function shouldUseMultipart(file, presignPath, options) {
  if (options && options.multipart === false) return false
  return presignPath === "/s3/params" && file.size >= MULTIPART_THRESHOLD_BYTES
}

// Options (all optional, so existing callers are unchanged):
//   onProgress(loadedBytes, totalBytes)
//   signal      an AbortSignal covering the presign request, the PUT(s) and
//               any retry backoff; the promise rejects with an AbortError
//   stallMs     per-request stall detector (see xhr.js)
//   multipart   false forces the single PUT
export function presignAndUpload(file, presignPath, options) {
  options = options || {}
  if (shouldUseMultipart(file, presignPath, options)) {
    return multipartUpload(file, options).catch(function (err) {
      // Only the explicit "not offered" answers fall back (kill switch,
      // not-eligible user). Auth failures and real errors stay fatal.
      if (err.code === "multipart_unavailable") {
        return singlePut(file, presignPath, options)
      }
      throw err
    })
  }
  return singlePut(file, presignPath, options)
}

function assertOk(response) {
  if (!response.ok) {
    var err = new Error("Request failed with status " + response.status)
    err.code = "presign_failed"
    err.status = response.status
    throw err
  }
  return response
}

function singlePut(file, presignPath, options) {
  var signal = options.signal
  if (signal && signal.aborted) return Promise.reject(abortError())

  var query = new URLSearchParams({
    filename: file.name,
    type: file.type || "application/octet-stream",
    size: file.size,
  })

  return fetch(presignPath + "?" + query.toString(), {
    headers: { Accept: "application/json" },
    credentials: "same-origin",
    signal: signal,
  })
    .then(assertOk)
    .then(function (response) {
      return response.json()
    })
    .then(function (presign) {
      return putWithRetry(presign, file, options, 0).then(function () {
        // Both cache and public_cache storages share the "cache" S3 prefix.
        var key = new URL(presign.url).pathname.match(/^\/cache\/(.+)/)[1]
        return {
          id: key,
          storage:
            presignPath.indexOf("public") !== -1 ? "public_cache" : "cache",
          metadata: {
            size: file.size,
            filename: file.name,
            mime_type: file.type,
          },
        }
      })
    })
}

// The presigned URL stays valid across a retry, so a network blip or a 5xx
// from storage gets one more try with the same URL.
function putWithRetry(presign, file, options, attempt) {
  return xhrPut(presign.url, {
    method: (presign.method || "put").toUpperCase(),
    headers: presign.headers || {},
    body: file,
    signal: options.signal,
    stallMs: options.stallMs,
    onProgress: options.onProgress,
  }).catch(function (err) {
    if (isAbortError(err)) throw err
    if (isRetryableStatus(err.status) && attempt + 1 < SINGLE_PUT_ATTEMPTS) {
      return putWithRetry(presign, file, options, attempt + 1)
    }
    throw err
  })
}
